import { ActionIcon, Group, InputBase } from "@mantine/core";
import { useEffect, useRef } from "react";

import { openedInputStyle } from "../../utils";
import { DropdownBaseTarget } from "../BaseTarget";
import { DropdownChevron } from "../Chevron";

import { IconX } from "@/components/icons";
import { getSmallerSize } from "@/utils/getSmallerSize";

import type { FC } from "react";
import type { DropdownBaseTargetProps } from "../BaseTarget";

export type DropdownSearchTargetProps = DropdownBaseTargetProps & {
  onChange?: (value: string) => void;
  query?: string;
};

export const DropdownSearchTarget: FC<DropdownSearchTargetProps> = ({
  clearable = false,
  children,
  disabled = false,
  iconVariant,
  onBlur,
  onClear,
  onChange,
  onClick,
  onFocus,
  opened = false,
  placeholder,
  position,
  query = "",
  size,
  value,
  ...props
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const currentSize = getSmallerSize(size);
  const isClearable = clearable && !!value;
  const inputStyle = position ? openedInputStyle[position] : undefined;

  useEffect(() => {
    if (opened) {
      ref.current?.focus();
    }
  }, [opened]);

  return opened ? (
    <InputBase
      ref={ref}
      disabled={disabled}
      onChange={(event) => onChange?.(event.currentTarget.value)}
      onClick={onClick}
      onFocus={onFocus}
      placeholder={opened ? placeholder : undefined}
      rightSection={
        <Group px="md" gap="sm" wrap="nowrap">
          {isClearable && (
            <ActionIcon
              color="red"
              size={currentSize}
              variant={iconVariant}
              onClick={onClear}
              disabled={disabled}
            >
              <IconX size={24} />
            </ActionIcon>
          )}
          <DropdownChevron
            disabled={disabled}
            onClick={onClick}
            opened={opened}
            size={currentSize}
            variant={iconVariant}
          />
        </Group>
      }
      rightSectionPointerEvents={isClearable ? undefined : "none"}
      rightSectionWidth="auto"
      styles={{
        input: opened ? inputStyle : undefined,
      }}
      value={query}
      {...props}
    />
  ) : (
    <DropdownBaseTarget
      clearable={clearable}
      disabled={disabled}
      iconVariant={iconVariant}
      onBlur={onBlur}
      onClear={onClear}
      onClick={onClick}
      onFocus={onFocus}
      placeholder={placeholder}
      size={size}
      value={value}
      {...props}
    >
      {children}
    </DropdownBaseTarget>
  );
};
